import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';
import { kaspreadsheetUrl } from '@/mocks/categories';

export default function BaseTaoWarehouse() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildArticleJsonLd({ headline: 'BaseTao Warehouse Guide: Storage, Consolidation & Shipping Workflow Explained', description: 'Everything about BaseTao warehouse services — storage timelines, fee structures, consolidation, and shipping workflow optimization.', path: '/basetao-warehouse' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Warehouse</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Warehouse Guide: Storage, Consolidation & Shipping Workflow Explained
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                Everything you need to know about BaseTao warehouse services — how storage works, when to consolidate shipments, warehouse locations, fee structures, and strategies to optimize your international shipping workflow.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>11 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">How the BaseTao Warehouse System Works</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  The warehouse system is one of the most valuable components of the BaseTao ecosystem for international buyers. When you order products from different suppliers, they ship to a partner warehouse rather than directly to your international address. The warehouse receives, inspects, and stores your items until you are ready to consolidate them into a single international shipment. This consolidation process is where the significant cost savings occur — instead of paying for multiple small international shipments, each with its own base rate and handling fees, you pay for one larger consolidated shipment with a much lower per-unit shipping cost.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  BaseTao Spreadsheet provides a visual warehouse dashboard that displays every item currently in storage, including arrival dates, storage duration, package dimensions, and consolidation recommendations. The system automatically calculates ongoing storage fees based on the applicable rate schedule and sends alerts when items are approaching fee-increase thresholds — typically after thirty, sixty, or ninety days of storage.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Warehouse Storage Timeline and Fees</h2>
                <div className="overflow-x-auto rounded-lg border border-background-200/60 mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Storage Period</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Fee Structure</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Recommended Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/60">
                      {[
                        { period: '0-30 days', fee: 'Free storage', action: 'Consolidate and ship within window' },
                        { period: '31-60 days', fee: 'Standard rate', action: 'Ship or prepare consolidation plan' },
                        { period: '61-90 days', fee: 'Elevated rate', action: 'Urgent: ship to avoid higher fees' },
                        { period: '90+ days', fee: 'Premium rate', action: 'Ship immediately or risk disposal fees' },
                      ].map((row) => (
                        <tr key={row.period} className="hover:bg-background-50 transition-colors">
                          <td className="px-4 py-2.5 font-medium text-foreground-800 whitespace-nowrap">{row.period}</td>
                          <td className="px-4 py-2.5 text-foreground-600 whitespace-nowrap">{row.fee}</td>
                          <td className="px-4 py-2.5 text-foreground-600">{row.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Smart Consolidation Strategies</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  The key to maximizing warehouse value is strategic consolidation timing. The ideal approach is to plan your purchases so that products from multiple suppliers arrive at the warehouse within the same one-to-two-week window. This allows you to consolidate everything into a single shipment well within the free storage period, avoiding storage fees entirely while still capturing the consolidation shipping discount.
                </p>
                <p className="text-sm md:text-base leading-relaxed">
                  For more resources on managing your international sourcing operations, visit our <Link to="/" className="text-primary-600 hover:text-primary-700 underline">homepage</Link> where we maintain comprehensive guides on all aspects of BaseTao Spreadsheet.
                </p>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Manage Your Warehouse</h2>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                    Open Warehouse Dashboard
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}